/**
 * CustomizationTab — CMS editor for the "Make It Yours" landing page section
 * Editable: title, subtitle, footerText, and feature boxes (icon, title, description)
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';

const ICON_OPTIONS = [
  { value: 'palette', label: 'Palette' },
  { value: 'image', label: 'Image' },
  { value: 'layout', label: 'Layout' },
  { value: 'users', label: 'Users' },
  { value: 'trophy', label: 'Trophy' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'piggybank', label: 'Piggy Bank' },
  { value: 'star', label: 'Star' },
  { value: 'shield', label: 'Shield' },
  { value: 'zap', label: 'Zap / Lightning' },
  { value: 'heart', label: 'Heart' },
  { value: 'settings', label: 'Settings / Cog' },
  { value: 'bell', label: 'Bell' },
  { value: 'message', label: 'Message' },
  { value: 'chart', label: 'Chart' },
  { value: 'clock', label: 'Clock' },
  { value: 'globe', label: 'Globe' },
  { value: 'lock', label: 'Lock' },
  { value: 'play', label: 'Play' },
  { value: 'help', label: 'Help Circle' },
  { value: 'phone', label: 'Smartphone' },
  { value: 'crown', label: 'Crown' },
  { value: 'check', label: 'Checkmark' },
];

export function CustomizationTab({ content, setContent }) {
  const customization = content?.customization || { title: '', subtitle: '', footerText: '', features: [] };

  const update = (field, value) => {
    setContent(prev => ({
      ...prev,
      customization: { ...prev.customization, [field]: value }
    }));
  };

  const updateFeature = (index, field, value) => {
    const features = [...(customization.features || [])];
    features[index] = { ...features[index], [field]: value };
    update('features', features);
  };

  const addFeature = () => {
    const features = [...(customization.features || [])];
    features.push({ icon: 'star', title: '', description: '' });
    update('features', features);
  };

  const removeFeature = (index) => {
    const features = (customization.features || []).filter((_, i) => i !== index);
    update('features', features);
  };

  const moveFeature = (from, to) => {
    if (to < 0 || to >= (customization.features || []).length) return;
    const features = [...(customization.features || [])];
    const [moved] = features.splice(from, 1);
    features.splice(to, 0, moved);
    update('features', features);
  };

  return (
    <Card data-testid="customization-tab">
      <CardHeader>
        <CardTitle>Make It Yours Section</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section heading */}
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Badge Text</label>
            <Input
              value={customization.badge || ''}
              onChange={e => update('badge', e.target.value)}
              placeholder="e.g., YOUR CLUB, YOUR WAY"
              data-testid="customization-badge-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Section Title</label>
            <Input
              value={customization.title || ''}
              onChange={e => update('title', e.target.value)}
              placeholder="Make It Yours"
              data-testid="customization-title-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Subtitle</label>
            <Input
              value={customization.subtitle || ''}
              onChange={e => update('subtitle', e.target.value)}
              placeholder="Customize every aspect of your team's experience"
              data-testid="customization-subtitle-input"
            />
          </div>
        </div>

        {/* Feature boxes */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Feature Boxes</label>
            <span className="text-xs text-gray-400">{(customization.features || []).length} items</span>
          </div>

          <div className="space-y-3" data-testid="customization-features-list">
            {(customization.features || []).map((feature, index) => (
              <div
                key={index}
                className="p-3 border border-gray-200 rounded-lg bg-white space-y-2"
                data-testid={`customization-feature-${index}`}
              >
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-gray-300 shrink-0" />
                  <span className="text-xs font-mono text-gray-400 w-4 shrink-0">{index + 1}</span>

                  {/* Icon picker */}
                  <Select
                    value={feature.icon || 'star'}
                    onValueChange={val => updateFeature(index, 'icon', val)}
                  >
                    <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`feature-icon-select-${index}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-xs">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex items-center gap-0.5 ml-auto shrink-0">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => moveFeature(index, index - 1)}
                      disabled={index === 0}
                      className="h-7 w-7 p-0"
                      data-testid={`feature-up-${index}`}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => moveFeature(index, index + 1)}
                      disabled={index === (customization.features || []).length - 1}
                      className="h-7 w-7 p-0"
                      data-testid={`feature-down-${index}`}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => removeFeature(index)}
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`feature-remove-${index}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                <Input
                  value={feature.title || ''}
                  onChange={e => updateFeature(index, 'title', e.target.value)}
                  placeholder="Feature title"
                  className="h-8 text-sm"
                  data-testid={`feature-title-${index}`}
                />
                <Textarea
                  value={feature.description || ''}
                  onChange={e => updateFeature(index, 'description', e.target.value)}
                  placeholder="Short description"
                  className="text-sm min-h-[60px] resize-none"
                  rows={2}
                  data-testid={`feature-desc-${index}`}
                />
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            onClick={addFeature}
            className="w-full mt-3 h-9 text-sm border-dashed"
            data-testid="add-feature-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Feature Box
          </Button>
        </div>

        {/* Footer text */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Section Footer Text</label>
          <Input
            value={customization.footerText || ''}
            onChange={e => update('footerText', e.target.value)}
            placeholder="Optional footer text for this section"
            data-testid="customization-footer-input"
          />
          <p className="text-xs text-gray-400 mt-1">Appears below the feature boxes</p>
        </div>
      </CardContent>
    </Card>
  );
}
